import React, { useContext, useState } from "react";
import { useHistory } from "react-router-dom";
import { DefaultButton, FontIcon } from "@fluentui/react";
import { ThemeContext } from "@fluentui/react-theme-provider";
import { ThemeProvider } from "styled-components";
import DocumentTitle from "react-document-title";
import CountryPrefixes from "@/utils/CountryPrefixes";
import { IdentitiesPath } from "@/screen/Identities/identitiesRoutes";
import { CompanyDetailsPath } from "@/screen/Companies/companiesRoutes";
import type { CompanyDetailsNavigationState } from "@/screen/Companies/companiesRoutes";
import CompanyOnboardingStepper, { CompanyOnboardingStep } from "@/screen/Companies/components/CompanyOnboardingStepper";
import CompanySectionCard from "@/screen/Companies/components/CompanySectionCard";
import { CompanyOnboardingStrings } from "@/screen/Companies/companyOnboarding.strings";
import { toCompanyOnboardingErrorMessages } from "@/screen/Companies/companyOnboardingErrors";
import CompanyOnboardingService from "@/services/CompanyOnboardingService";
import type { CompanyDraftResultDto } from "@/services/CompanyOnboardingService";
import { buildCreateCompanyDraftRequest, CompanyFirstYearPrice, CompanyRenewalPrice, formatEuros, getInitialCompanySubscriptionFormData, getInvalidCompanySubscriptionFields } from "@/screen/Companies/CompanySubscription/companySubscriptionForm";
import type { CompanySubscriptionFormData } from "@/screen/Companies/CompanySubscription/companySubscriptionForm";
import { Cont, MainCont, BackLink, Header, WarningBanner, SubmitNotice, ErrorNotice, SubscriptionLayout, OrderSummary, OrderNote } from "@/screen/Companies/Styles/CompanyOnboardingStyles";
import { Field, FieldError, Input, Select, TwoFieldRow } from "@/screen/Companies/Styles/CompanyFormFieldStyles";

const PhonePrefixOptions = CountryPrefixes
    .filter((country) => country.phone_code)
    .map((country) => ({ value: country.phone_code, text: `${country.name} (${country.phone_code})` }));

// Paso 2 del alta de empresa: empresa titular de la suscripción y pago. De momento solo maqueta, sin backend.
const CompanySubscriptionScreen: React.FC = () =>
{
    const themeContext = useContext(ThemeContext);
    const history = useHistory();
    const [formData, setFormData] = useState<CompanySubscriptionFormData>(getInitialCompanySubscriptionFormData);
    const [touched, setTouched] = useState<Partial<Record<keyof CompanySubscriptionFormData, boolean>>>({});
    const [paymentMessage, setPaymentMessage] = useState<string | null>(null);
    const [paymentErrors, setPaymentErrors] = useState<string[]>([]);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [companyIdentityId, setCompanyIdentityId] = useState<number | null>(null);

    const invalidFields = getInvalidCompanySubscriptionFields(formData);
    const showError = (field: keyof CompanySubscriptionFormData): boolean => !!touched[field] && invalidFields.includes(field);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void =>
    {
        const {name, value} = e.target;
        setFormData((prev) => ({...prev, [name]: value}));
        setPaymentMessage(null);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>): void =>
    {
        const name = e.target.name as keyof CompanySubscriptionFormData;
        setTouched((prev) => ({...prev, [name]: true}));
    };

    // Se guarda la empresa como borrador y después se pagará.
    // TODO: crear la suscripción de empresa y redirigir al pago con Inespay (pendiente de las decisiones de negocio)
    const handlePayment = async (): Promise<void> =>
    {
        setIsSaving(true);
        setPaymentErrors([]);
        setPaymentMessage(null);
        const response = await CompanyOnboardingService.createCompanyDraft(buildCreateCompanyDraftRequest(formData));
        setIsSaving(false);

        if(response.success)
        {
            setCompanyIdentityId((response.data as CompanyDraftResultDto | null)?.companyIdentityId ?? null);
            setPaymentMessage(CompanyOnboardingStrings.paymentPending);
            return;
        }

        setPaymentErrors(toCompanyOnboardingErrorMessages(response.errors, CompanyOnboardingStrings.companyDraftGenericError));
    };

    return(
        <DocumentTitle title={`IronIA - ${CompanyOnboardingStrings.subscriptionTitle}`}>
            <Cont theme={themeContext}>
                <ThemeProvider theme={themeContext}>
                    <MainCont>
                        <BackLink type="button" onClick={() => history.push(IdentitiesPath)}>
                            <FontIcon iconName="ChevronLeft" />
                            {CompanyOnboardingStrings.back}
                        </BackLink>
                        <Header>
                            <h1>{CompanyOnboardingStrings.subscriptionTitle}</h1>
                            <h2>{CompanyOnboardingStrings.subscriptionSubtitle}</h2>
                        </Header>
                        <WarningBanner role="note">
                            <FontIcon iconName="Warning" />
                            <span>{CompanyOnboardingStrings.usTaxResidenceWarning}</span>
                        </WarningBanner>
                        <CompanyOnboardingStepper currentStep={CompanyOnboardingStep.Subscription} />

                        <SubscriptionLayout>
                            <CompanySectionCard number={1} title={CompanyOnboardingStrings.subscriptionHolderTitle} description={CompanyOnboardingStrings.subscriptionHolderDescription}>
                                <Field>
                                    <span>Razón social <b>*</b></span>
                                    <Input type="text" name="businessName" value={formData.businessName} onChange={handleChange} onBlur={handleBlur} maxLength={200} placeholder="Ej. Inversiones Ejemplo, S.L." autoComplete="off" />
                                    {showError("businessName") && <FieldError>La razón social es obligatoria.</FieldError>}
                                </Field>
                                <TwoFieldRow>
                                    <Field>
                                        <span>Nº de identificación fiscal <b>*</b></span>
                                        <Input type="text" name="taxId" value={formData.taxId} onChange={handleChange} onBlur={handleBlur} maxLength={9} placeholder="B12345678" autoComplete="off" />
                                        {showError("taxId") && <FieldError>El CIF no es válido.</FieldError>}
                                    </Field>
                                    <Field>
                                        <span>Fecha de constitución <b>*</b></span>
                                        <Input type="date" name="incorporationDate" value={formData.incorporationDate} onChange={handleChange} onBlur={handleBlur} />
                                        {showError("incorporationDate") && <FieldError>Indica una fecha de constitución que no sea futura.</FieldError>}
                                    </Field>
                                </TwoFieldRow>
                                <Field>
                                    <span>Correo electrónico <b>*</b></span>
                                    <Input type="email" name="email" value={formData.email} onChange={handleChange} onBlur={handleBlur} maxLength={255} placeholder="administracion@empresa.com" autoComplete="off" />
                                    {showError("email") && <FieldError>El correo electrónico no es válido.</FieldError>}
                                </Field>
                                <TwoFieldRow>
                                    <Field>
                                        <span>Prefijo (país) <b>*</b></span>
                                        <Select name="countryPrefix" value={formData.countryPrefix} onChange={handleChange} onBlur={handleBlur}>
                                            {PhonePrefixOptions.map((option, index) => <option key={`${option.value}-${index}`} value={option.value}>{option.text}</option>)}
                                        </Select>
                                    </Field>
                                    <Field>
                                        <span>Número teléfono <b>*</b></span>
                                        <Input type="tel" name="phone" value={formData.phone} onChange={handleChange} onBlur={handleBlur} maxLength={15} placeholder="600 000 000" autoComplete="off" />
                                        {showError("phone") && <FieldError>El teléfono no es válido.</FieldError>}
                                    </Field>
                                </TwoFieldRow>
                            </CompanySectionCard>

                            <div>
                                <OrderSummary>
                                    <h3>{CompanyOnboardingStrings.orderSummaryTitle}</h3>
                                    <dl>
                                        <div><dt>{CompanyOnboardingStrings.orderPlan}</dt><dd>{CompanyOnboardingStrings.orderPlanName}</dd></div>
                                        <hr />
                                        <div><dt>{CompanyOnboardingStrings.orderFirstYear}</dt><dd>{formatEuros(CompanyFirstYearPrice)}</dd></div>
                                        <div className="small"><dt>{CompanyOnboardingStrings.orderRenewal}</dt><dd>{formatEuros(CompanyRenewalPrice)}</dd></div>
                                        <hr />
                                        <div className="total"><dt>{CompanyOnboardingStrings.orderTotalToday}</dt><dd>{formatEuros(CompanyFirstYearPrice)}</dd></div>
                                    </dl>
                                    <button type="button" onClick={handlePayment} disabled={isSaving || invalidFields.length > 0}>
                                        {isSaving ? CompanyOnboardingStrings.savingCompany : CompanyOnboardingStrings.makePayment}
                                    </button>
                                </OrderSummary>
                                <OrderNote>{CompanyOnboardingStrings.afterPaymentInfo}</OrderNote>
                                {paymentErrors.length > 0 &&
                                (
                                    <ErrorNotice role="alert">
                                        {paymentErrors.map((message, index) => <p key={index}>{message}</p>)}
                                    </ErrorNotice>
                                )}
                                {paymentMessage && <SubmitNotice role="status">{paymentMessage}</SubmitNotice>}
                                {/* Mientras no hay pago se sigue al paso 3 con la empresa guardada como borrador */}
                                {companyIdentityId &&
                                (
                                    <DefaultButton className="button tiny-primary-button" style={{marginTop: "16px"}}
                                        onClick={() => history.push(CompanyDetailsPath, { companyIdentityId } as CompanyDetailsNavigationState)}
                                    >
                                        {CompanyOnboardingStrings.continueToCompanyDetails}
                                    </DefaultButton>
                                )}
                            </div>
                        </SubscriptionLayout>
                    </MainCont>
                </ThemeProvider>
            </Cont>
        </DocumentTitle>
    );
};

export default CompanySubscriptionScreen;
